import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';

import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import useAlert from '../../../Alerts/useAlert';
import usePrivileges from '../../../Auth/usePrivileges';

import GradedCommentDialog from "../../../Dialogs/GradedCommentDialog";
import GradedWarningDialog from '../../../Dialogs/GradedWarningDialog';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { LotStatus } from '../../../Types/Lot';

function GradeLotButton() {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();

    const {
        lotDetail: {
            summary: {
                businessUnitCode,
                currentStatus,
                processingStatus,
                isLocked
            },
            warnings
        }, updateLotStatus
    } = useContext(LotDetailContext);
    const { canGrade } = usePrivileges(businessUnitCode);

    const [disabled, setDisabled] = useState<boolean>(false);
    const [showGradeCommentDialog, setShowGradeCommentDialog] = useState(false);

    const [gradedReasons, setGradedReasons] = useState([]);

    const getGradedReasons = useCallback(async () => {
        let url = `api/v1/gradedreason/?includeDeleted=false`;
        if (businessUnitCode)
            url += `&businessUnitCode=${businessUnitCode}`;

        await fetchDataAuthenticated(url)
            .then(response => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw new Error(`Failed to get graded reason codes for business unit ${businessUnitCode}`);
                }
            })
            .then(data => setGradedReasons(data))
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })

    }, [businessUnitCode, fetchDataAuthenticated, setAlert]);

    useEffect(() => {
        switch (processingStatus) {
            case "Processing":
            case "Queued":
                setDisabled(true);
                break;
            default:
                switch (true) {
                    case currentStatus !== "ReadyToGrade":
                        setDisabled(true);
                        break;
                    default:
                        setDisabled(false);
                }
        }

        if (isLocked) {
            setDisabled(true);
        }
    }, [currentStatus, processingStatus, isLocked]);

    useEffect(() => {
        getGradedReasons();
    }, [getGradedReasons]);

    const handleGradeCommentSave = (comment: string, gradedReason: string) => {
        updateLotStatus(LotStatus.Graded, comment, gradedReason, "Queued the lot for grading");
    };

    return (
        <>
            <Button variant="primary"
                onClick={() => setShowGradeCommentDialog(true)}
                hidden={currentStatus === "Graded"}
                disabled={disabled || !canGrade}>
                {t('LotDetails.Summary.ConfirmGraded')}
            </Button>
            <GradedCommentDialog
                showDialog={warnings.length === 0 && showGradeCommentDialog}
                closeDialog={() => setShowGradeCommentDialog(false)}
                onSave={handleGradeCommentSave}
                gradedReasons={gradedReasons} />
            <GradedWarningDialog
                showDialog={warnings.length > 0 && showGradeCommentDialog}
                closeDialog={() => setShowGradeCommentDialog(false)}
                onSave={handleGradeCommentSave}
                warnings={warnings}
                gradedReasons={gradedReasons} />
        </>
    );
}

export default GradeLotButton;